import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Globe, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin toggle: allow anonymous visitors to view /u/:userId profile pages.
 * When off, /u/:id requires auth and unauthenticated visitors are bounced to /login.
 */
export default function PublicProfilesCard() {
  const { settings, refreshSettings } = useSettings();
  const [enabled, setEnabled] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    setEnabled(!!settings?.public_profiles_enabled);
  }, [settings?.public_profiles_enabled]);

  const toggle = async () => {
    const next = !enabled;
    setToggling(true);
    // Optimistic update
    setEnabled(next);
    try {
      await api.patch("/settings", { public_profiles_enabled: next });
      await refreshSettings();
      toast.success(next ? "Public profiles enabled" : "Public profiles disabled");
    } catch (err) {
      setEnabled(!next);
      toast.error(formatApiError(err));
    } finally {
      setToggling(false);
    }
  };

  const sampleUrl = `${window.location.origin}/u/<user-id>`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(sampleUrl);
      toast.success("Sample URL copied");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="public-profiles-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 flex items-center justify-center" style={{ background: "var(--brand, #0D9488)" }}>
          <Globe className="w-4 h-4 text-black" />
        </div>
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Sharing</div>
          <div className="font-display font-black uppercase tracking-tight text-xl">Public Profile Pages</div>
        </div>
        <div className="ml-auto">
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            disabled={toggling}
            onClick={toggle}
            data-testid="public-profiles-toggle"
            className={`relative inline-flex h-6 w-11 items-center transition-colors ${
              enabled ? "bg-[color:var(--brand,#0D9488)]" : "bg-zinc-800"
            } disabled:opacity-50`}
          >
            <span
              className={`inline-block h-4 w-4 transform bg-white transition-transform ${
                enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      <p className="text-slate-600 text-sm max-w-2xl mb-4">
        When enabled, each user's <span className="font-mono-num text-slate-900">/u/&lt;user-id&gt;</span> page is viewable
        without a login — perfect for sharing on X, Reddit, or wherever. Anonymous visitors see the profile stats + recent
        activity but never any private data. When disabled, unauthenticated visitors are redirected to sign in.
      </p>

      <div className="border border-slate-200 bg-slate-50 p-3 flex items-center gap-2" data-testid="public-profiles-status">
        <span
          className={`inline-block w-2 h-2 ${enabled ? "bg-[color:var(--brand,#0D9488)]" : "bg-zinc-600"} animate-pulse`}
        />
        <span className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
          Status: <span className="text-slate-900">{enabled ? "Enabled — profiles are public" : "Disabled — profiles require login"}</span>
        </span>
      </div>

      {enabled && (
        <div className="mt-4 space-y-2" data-testid="public-profiles-sample">
          <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-slate-500">// Sample URL format</div>
          <div className="flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 h-10">
            <ExternalLink className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <code className="font-mono-num text-xs text-slate-900 truncate flex-1">{sampleUrl}</code>
            <Button
              type="button"
              onClick={copyUrl}
              variant="ghost"
              className="h-8 rounded-none text-slate-600 hover:text-[color:var(--brand,#0D9488)] hover:bg-transparent px-2"
              data-testid="public-profiles-copy"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="text-[10px] font-mono-num text-slate-500 uppercase tracking-widest">
            Users can grab their own share URL from their Profile page.
          </div>
        </div>
      )}
    </Card>
  );
}
